<template>
  <div class="bg-background min-h-screen pb-12 overflow-x-hidden">
    <!-- Toast Notification -->

    <Toast
      :show="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      @close="showToast = false"
    />

    <!-- Loading State -->

    <div v-if="loading" class="w-full px-4 sm:px-6 py-12 min-w-0">
      <div class="bg-card rounded-lg shadow-sm p-8 text-center">
        <div class="mb-4">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"
          ></div>
        </div>

        <h2 class="text-xl font-semibold text-foreground mb-2">
          Loading Job Details
        </h2>

        <p class="text-muted-foreground">Please wait...</p>
      </div>
    </div>

    <!-- Error State -->

    <div v-else-if="error" class="w-full px-4 sm:px-6 py-12 min-w-0">
      <div class="bg-card rounded-lg shadow-sm p-8 text-center">
        <div class="mb-4">
          <i class="fas fa-exclamation-triangle text-4xl text-red-500"></i>
        </div>

        <h2 class="text-2xl font-semibold text-foreground mb-2">Job Not Found</h2>

        <p class="text-muted-foreground mb-6">{{ error }}</p>

        <div class="flex gap-4 justify-center">
          <button
            @click="router.push('/job-filters')"
            class="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition duration-200"
          >
            <i class="fas fa-arrow-left mr-2"></i>

            Browse All Jobs
          </button>

          <button
            @click="fetchJobDetails"
            class="px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-muted/50 transition duration-200"
          >
            <i class="fas fa-redo mr-2"></i>

            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Gated Teaser State (anonymous visitor, recent OLJ job) -->

    <div v-else-if="job.is_gated" class="w-full px-4 sm:px-6 py-12 min-w-0 max-w-2xl mx-auto">
      <div class="bg-card rounded-2xl border border-border shadow-sm p-6 sm:p-8">
        <div class="mb-4 flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide">
          <i class="fas fa-lock"></i>
          <span>Sign up to view the full post</span>
        </div>

        <h1 class="text-xl sm:text-2xl font-semibold text-foreground mb-3">{{ job.title }}</h1>

        <div class="flex flex-wrap gap-2 mb-4 text-xs">
          <span v-if="job.job_type" class="px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{{ job.job_type }}</span>
          <span v-if="job.salary" class="px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{{ job.salary }}</span>
          <span v-if="job.employer_industry" class="px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{{ job.employer_industry }}</span>
          <span v-if="job.employer_company_size" class="px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{{ job.employer_company_size }} employees</span>
        </div>

        <CollapsibleTagList
          v-if="job.tags && job.tags.length"
          :tags="job.tags"
          tag-class="px-2 py-0.5 rounded bg-primary/10 text-primary text-[11px] font-medium"
          class="mb-4"
        />

        <p v-if="job.description_preview" class="text-sm text-muted-foreground leading-relaxed mb-6">
          {{ job.description_preview }}<span v-if="job.description_preview.length >= 200">&hellip;</span>
        </p>

        <div class="border-t border-border pt-5">
          <p class="text-sm text-foreground font-medium mb-3">Create your free account to see the full post and apply.</p>
          <router-link
            :to="`/register?redirect=${encodeURIComponent(route.fullPath)}`"
            class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition duration-200"
          >
            Create Free Account
            <i class="fas fa-arrow-right text-xs"></i>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Main Content -->

    <div v-else-if="job.title" class="w-full px-4 sm:px-6 py-6 sm:py-8 min-w-0">

      <!-- Breadcrumb -->

      <nav class="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
        <button
          v-if="showBackButton"
          @click="$router.back()"
          class="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
        >
          <i class="fas fa-arrow-left text-[10px]"></i>
          {{ backLabel }}
        </button>
        <router-link v-else to="/job-filters" class="hover:text-foreground">Find Jobs</router-link>
        <i class="fas fa-chevron-right text-[9px]"></i>
        <span class="text-foreground font-medium truncate max-w-[240px] sm:max-w-none">{{ job.title }}</span>
      </nav>

      <!-- Job Header Card -->

      <section class="rounded-2xl border border-border bg-card shadow-sm p-5 sm:p-6 mb-6">
        <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-5">

          <!-- Logo + Title -->

          <div class="flex gap-4 sm:gap-5">
            <div class="h-14 w-14 sm:h-16 sm:w-16 shrink-0 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center overflow-hidden">
              <img
                v-if="employerLogoUrl && !logoLoadError"
                :src="employerLogoUrl"
                :alt="job.employer?.name || 'Employer'"
                class="w-full h-full object-cover"
                @error="logoLoadError = true"
              />
              <span v-else class="text-primary text-lg font-bold">{{ employerInitials }}</span>
            </div>

            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap mb-1.5">
                <span
                  v-if="job.closed"
                  class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-300 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span> Closed
                </span>
              </div>

              <h1 class="text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
                {{ job.title }}
              </h1>

              <div class="mt-1 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                <span class="font-medium">{{ job.employer?.name || 'Employer' }}</span>
                <span v-if="job.employer?.total_posts || job.employer_total_posts" class="text-xs text-muted-foreground">({{ (job.employer?.total_posts || job.employer_total_posts) }} job{{ (job.employer?.total_posts || job.employer_total_posts) === 1 ? '' : 's' }} posted)</span>
              </div>

              <CollapsibleTagList
                v-if="job.tags && job.tags.length"
                :tags="job.tags"
                tag-class="text-[11px] px-2.5 py-1 rounded-full bg-muted text-foreground border border-border"
                class="mt-3"
              />
            </div>
          </div>

          <!-- Header Actions -->

          <div class="flex items-center gap-2 shrink-0 flex-wrap">
            <button
              v-if="job.closed"
              class="inline-flex items-center justify-center gap-2 rounded-lg bg-red-500/15 text-red-700 dark:text-red-300 px-4 py-2 text-sm font-medium whitespace-nowrap cursor-default"
              disabled
            >
              <i class="fas fa-times-circle text-xs"></i>
              Job Closed
            </button>

            <button
              v-else-if="job.applied"
              class="inline-flex items-center justify-center gap-2 rounded-lg bg-green-500/15 text-green-700 dark:text-green-300 px-4 py-2 text-sm font-medium whitespace-nowrap cursor-default"
              disabled
            >
              <i class="fas fa-check-circle text-xs"></i>
              Applied
            </button>

            <button
              v-else
              @click="handleApplyClick"
              class="inline-flex items-center justify-center gap-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
            >
              <i class="fas fa-paper-plane text-xs"></i>
              Apply Now
            </button>

            <button
              v-if="job.link"
              @click="openExternalJobPost"
              :title="`View original post on ${externalPlatformLabel}`"
              class="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-xs font-medium whitespace-nowrap"
            >
              <i class="fas fa-external-link-alt text-xs"></i>
              Job Post
            </button>

            <button
              @click="saveJob"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors"
              :class="isSaved
                ? 'bg-primary/10 text-primary border-primary/20 hover:bg-muted/50'
                : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'"
              :title="isSaved ? 'Remove from Saved' : 'Save Job'"
            >
              <i :class="isSaved ? 'fas fa-bookmark text-primary' : 'far fa-bookmark'" class="text-xs"></i>
            </button>

            <button
              @click="shareJob"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title="Share"
            >
              <i class="fas fa-share-alt text-xs"></i>
            </button>
          </div>
        </div>
      </section>

      <!-- Two-Column Grid -->

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- Left Column: Job meta + Description -->

        <div class="lg:col-span-2 space-y-6">

          <!-- Job Meta Strip -->

          <div class="rounded-2xl border border-border bg-card shadow-sm p-5">
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div class="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Job Type</div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-briefcase text-primary text-xs"></i>
                  <span class="text-sm font-medium">{{ job.job_type || 'Not specified' }}</span>
                </div>
              </div>
              <div>
                <div class="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Salary</div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-dollar-sign text-emerald-600 text-xs"></i>
                  <span class="text-sm font-medium tabular-nums">{{ job.salary || 'Not specified' }}</span>
                </div>
              </div>
              <div>
                <div class="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Posted</div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-calendar text-amber-600 text-xs"></i>
                  <span class="text-sm font-medium">{{ formatDate(job.posted_at || job.date_posted) }}</span>
                </div>
              </div>
              <div>
                <div class="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Employer</div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-building text-primary text-xs"></i>
                  <span class="text-sm font-medium truncate">{{ job.employer?.name || 'Not specified' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Job Description Card -->

          <div class="rounded-2xl border border-border bg-card shadow-sm">
            <div class="px-5 sm:px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
              <h2 class="text-base font-semibold">Job Description</h2>
            </div>
            <div class="px-5 sm:px-6 py-5">
              <div
                class="prose max-w-none text-foreground prose-headings:font-semibold prose-p:my-2 prose-li:my-0.5 job-description-content"
                v-html="formattedDescription"
              ></div>
            </div>
          </div>

        </div>

        <!-- Right Column: Apply Form + About Employer -->

        <div class="lg:col-span-1 space-y-6">

          <!-- Apply Form Card -->

          <div
            id="apply-form"
            class="rounded-2xl border border-border bg-card shadow-sm overflow-hidden transition-all duration-300"
          >

            <!-- Closed state -->

            <template v-if="job.closed">
              <div class="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-br from-red-500/5 to-transparent">
                <h2 class="text-base font-semibold leading-tight">This job is closed</h2>
                <p class="text-[11px] text-muted-foreground mt-0.5">Applications are no longer being accepted.</p>
              </div>
              <div class="p-5">
                <div class="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300">
                  <i class="fas fa-times-circle text-lg shrink-0"></i>
                  <p class="text-sm font-medium">This job is no longer available.</p>
                </div>
              </div>
            </template>

            <!-- Applied state -->

            <template v-else-if="job.applied">
              <div class="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-br from-emerald-500/5 to-transparent">
                <h2 class="text-base font-semibold leading-tight">Application submitted</h2>
                <p class="text-[11px] text-muted-foreground mt-0.5">You have already applied for this position.</p>
              </div>
              <div class="p-5">
                <div class="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300">
                  <i class="fas fa-check-circle text-lg shrink-0"></i>
                  <p class="text-sm font-medium">You have already applied for this job.</p>
                </div>
              </div>
            </template>

            <!-- Full Apply Form -->

            <template v-else>
              <!-- Tab Navigation -->
              <div class="flex border-b border-border">
                <button
                  type="button"
                  @click="activeApplyTab = 'cover-letter'"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors relative"
                  :class="activeApplyTab === 'cover-letter' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'"
                >
                  <span class="flex items-center justify-center gap-1.5">
                    <i class="fas fa-file-alt text-xs"></i>
                    Cover Letter
                  </span>
                  <span v-if="activeApplyTab === 'cover-letter'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-sm"></span>
                </button>
                <button
                  type="button"
                  @click="isAdminUser && (activeApplyTab = 'resume', fetchResumePdf())"
                  :disabled="!isAdminUser"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors relative disabled:cursor-not-allowed"
                  :class="activeApplyTab === 'resume' ? 'text-primary' : 'text-foreground'"
                >
                  <span class="flex items-center justify-center gap-1.5">
                    <i class="fas fa-id-card text-xs"></i>
                    Resume
                    <span v-if="!isAdminUser" class="text-[9px] font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full leading-none">Soon</span>
                  </span>
                  <span v-if="activeApplyTab === 'resume'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-sm"></span>
                </button>
              </div>

              <!-- Cover Letter Tab: the letter itself is written in the side panel -->
              <div v-show="activeApplyTab === 'cover-letter'" class="p-5 space-y-4">
                <div class="flex items-start gap-3">
                  <div class="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <i class="fas fa-wand-magic-sparkles text-sm"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-foreground">Cover letter</p>
                    <p class="text-xs text-muted-foreground mt-0.5">
                      AI writes one for this job as soon as you open it. Chat with it to change anything, then {{ isExternalJob ? 'copy it to the job post' : 'submit' }}.
                    </p>
                  </div>
                </div>

                <!-- OLJ jobs, saved login rejected by OnlineJobs.ph: reconnect first -->
                <template v-if="isLoggedIn && oljUnlocked && !isExternalJob && oljConnected && oljNeedsReconnect">
                  <div
                    class="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
                    data-test="olj-reconnect-warning"
                  >
                    <i class="fas fa-exclamation-triangle mt-0.5 text-amber-600 dark:text-amber-400"></i>
                    <span>Your OnlineJobs.ph account needs to be reconnected. Applications and apply points won't work until you reconnect.</span>
                  </div>
                  <router-link
                    :to="OLJ_RECONNECT_ROUTE"
                    :class="jobDetailsTourHighlightClass('olj')"
                    class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-amber-600 transition-colors"
                    data-test="olj-reconnect-button"
                  >
                    <i class="fas fa-plug text-xs"></i>
                    Reconnect OnlineJobs.ph to Apply
                  </router-link>
                </template>

                <!-- Applied on the job's own site with the Chrome extension: Upwork, JobStreet...
                     and OnlineJobs.ph too unless their account is connected -->
                <button
                  v-else-if="showApplyOnSite"
                  type="button"
                  @click="openExternalJobPost"
                  :class="jobDetailsTourHighlightClass('olj')"
                  class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 shadow-sm transition-colors"
                  data-test="apply-on-site"
                >
                  <i class="fas fa-arrow-up-right-from-square text-xs"></i>
                  {{ siteName ? `Apply on ${siteName}` : 'Open job post' }}
                </button>

                <div class="flex gap-2">
                  <button
                    type="button"
                    @click="handleApplyClick"
                    :class="[
                      jobDetailsTourHighlightClass('generate'),
                      isLoggedIn && oljUnlocked && !isExternalJob && oljConnected && !oljNeedsReconnect ? jobDetailsTourHighlightClass('olj') : '',
                      showApplyOnSite || (isLoggedIn && oljUnlocked && !isExternalJob && oljConnected && oljNeedsReconnect)
                        ? 'border border-border bg-card text-foreground hover:bg-accent'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
                    ]"
                    class="flex-[4] min-w-0 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
                    data-test="open-cover-letter-panel"
                  >
                    <i class="fas fa-wand-magic-sparkles text-xs"></i>
                    <span class="min-w-0 text-center leading-snug">{{ coverLetterButtonLabel }}</span>
                  </button>
                  <button
                    v-if="!showApplyOnSite"
                    type="button"
                    @click="openExternalJobPost"
                    title="View Job Post"
                    class="flex-1 shrink-0 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <i class="fas fa-external-link-alt text-xs"></i>
                  </button>
                </div>
              </div>

              <!-- Resume Tab -->
              <div v-show="activeApplyTab === 'resume'" class="p-5 space-y-5">

                <!-- Current Resume -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <div class="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Your Current Resume</div>
                    <span v-if="resumePdfPageCount" class="text-[11px] text-muted-foreground">{{ resumePdfPageCount }} page{{ resumePdfPageCount === 1 ? '' : 's' }}</span>
                  </div>

                  <!-- Loading -->
                  <div v-if="loadingResumePdf" class="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                    <i class="fas fa-spinner fa-spin text-lg"></i>
                    <span class="text-xs">Loading resume…</span>
                  </div>

                  <!-- PDF viewer -->
                  <div
                    v-else-if="resumePdfSource"
                    class="overflow-y-auto rounded-lg border border-border bg-muted shadow-inner"
                    style="max-height: 480px"
                  >
                    <vue-pdf-embed
                      :source="resumePdfSource"
                      class="w-full"
                      @rendered="(e) => resumePdfPageCount = e"
                    />
                  </div>

                  <!-- No resume -->
                  <p v-else-if="!userResumes[0]" class="text-xs text-muted-foreground py-2">
                    No resume uploaded yet.
                    <router-link to="/settings" class="text-primary hover:underline font-medium">Upload one in Settings</router-link>
                    to enable AI tailoring.
                  </p>

                  <!-- Load failed / empty -->
                  <div v-else class="text-xs text-muted-foreground py-4 text-center">
                    <p>Could not preview resume.</p>
                    <a v-if="userResumes[0]" :href="userResumes[0].file" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">Download to view</a>
                  </div>
                </div>

                <!-- Divider + Generate -->
                <div class="flex items-end justify-between gap-2">
                  <div>
                    <div class="flex items-center gap-1.5 mb-0.5">
                      <span class="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Tailored Version</span>
                    </div>
                  </div>
                  <span :title="generateWithAiButtonTitle" class="shrink-0">
                    <button
                      type="button"
                      @click="generateTailoredResume"
                      :disabled="generatingTailoredResume || !canGenerateAiCoverLetterNow"
                      class="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground px-3 py-2 hover:bg-primary/90 shadow-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    >
                      <i v-if="generatingTailoredResume" class="fas fa-spinner fa-spin text-[11px]"></i>
                      <i v-else class="fas fa-wand-magic-sparkles text-[11px]"></i>
                      {{ generatingTailoredResume ? 'Generating...' : 'Generate with AI' }}
                    </button>
                  </span>
                </div>

                <!-- Upgrade hint -->
                <p v-if="showAiCoverLetterUpgradeHint" class="text-xs text-muted-foreground">
                  {{ expiredPlanLabel }} expired. To tailor your resume with AI,
                  <router-link to="/subscription/plans" class="text-primary hover:underline font-medium">{{ expiredPlanLabel === 'Free plan' ? 'subscribe to Basic or Premium' : 'renew your plan' }}</router-link>.
                </p>

                <!-- Quota exhausted hint -->
                <p
                  v-else-if="isFreeTrialAiLimited && trialAiQuotaRemaining === 0 && trialAiQuotaLimit != null"
                  class="text-xs text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2"
                >
                  <span class="font-medium">AI limit reached.</span>
                  <router-link to="/subscription/plans" class="text-primary hover:underline font-medium ml-1">Upgrade your plan</router-link>
                  to generate more.
                </p>

                <!-- Tailored result lives in the side panel (opens automatically once generated) -->
                <button
                  v-if="tailoredResumeContent"
                  type="button"
                  @click="openTailoredResumePanel"
                  class="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-accent/40 px-3 py-2.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <i class="fas fa-file-lines text-[11px]"></i>
                  View Tailored Resume
                  <span v-if="tailoredResumeRelevance" class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" :class="relevanceBadgeClass">
                    Match: {{ tailoredResumeRelevance.score }}%
                  </span>
                </button>

                <!-- Empty state -->
                <p v-else class="text-xs text-muted-foreground">
                  Click Generate with AI to get a version of your resume rewritten to highlight the skills and experience most relevant to this job.
                </p>
              </div>
            </template>
          </div>

          <!-- About the Employer Card -->

          <div class="rounded-2xl border border-border bg-card shadow-sm p-5">
            <h3 class="text-base font-semibold mb-3">About the Employer</h3>

            <div class="flex items-center gap-3 px-2 py-2">
              <div class="h-12 w-12 shrink-0 rounded-lg bg-primary/10 border border-primary/20 grid place-items-center overflow-hidden">
                <img
                  v-if="employerLogoUrl && !logoLoadError"
                  :src="employerLogoUrl"
                  :alt="job.employer?.name || 'Employer'"
                  class="w-full h-full object-cover"
                  @error="logoLoadError = true"
                />
                <span v-else class="text-primary text-sm font-bold">{{ employerInitials }}</span>
              </div>
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-sm">{{ job.employer?.name || 'Employer' }}</div>
                <div class="text-[11px] text-muted-foreground">
                  <span><i class="fas fa-briefcase text-[9px] mr-1"></i>{{ job.employer?.total_posts || job.employer_total_posts || 0 }} jobs posted</span>
                </div>
              </div>
            </div>

            <p class="text-xs text-muted-foreground mt-3 leading-relaxed">
              This employer has posted multiple jobs on our platform.
            </p>
          </div>

        </div>

      </div>
    </div>

    <!-- Tailored Resume side panel: opens once Generate finishes. Everything related to the
         result — formatted preview (with in-place click-to-edit), zoom, copy, download — lives
         here instead of being buried inline in the Resume tab. -->
    <transition name="tailored-panel-fade">
      <div
        v-if="tailoredResumePanelOpen"
        class="fixed inset-0 bg-black/40 z-40"
        @click="closeTailoredResumePanel"
      ></div>
    </transition>
    <transition name="tailored-panel-slide">
      <div
        v-show="tailoredResumePanelOpen"
        class="fixed inset-y-0 right-0 z-50 w-full sm:w-[85vw] sm:max-w-3xl bg-card border-l border-border shadow-xl flex flex-col"
      >
        <div class="flex items-center justify-between gap-2 px-5 py-4 border-b border-border shrink-0">
          <div>
            <div class="flex items-center gap-1.5">
              <h3 class="text-lg font-semibold text-foreground">Tailored Resume</h3>
              <span
                v-if="tailoredResumeRelevance"
                :title="tailoredResumeRelevance.missing_keywords?.length ? `Missing from your resume: ${tailoredResumeRelevance.missing_keywords.join(', ')}` : 'Your resume already covers this job well'"
                class="text-xs font-semibold px-2 py-1 rounded-full"
                :class="relevanceBadgeClass"
              >
                Match: {{ tailoredResumeRelevance.score }}%
              </span>
            </div>
            <p v-if="tailoredResumeChanges.length" class="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              <span class="flex items-center gap-1.5">
                <span class="inline-block w-3 h-3 rounded-sm bg-amber-300 border border-amber-500"></span>
                Highlighted = AI-changed, click to edit
              </span>
              <span v-if="tailoredResumeChangesView.some((c) => c.suspiciousTerms?.length)" class="flex items-center gap-1.5">
                <span class="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-300"></span>
                Unverified term
              </span>
            </p>
          </div>
          <button type="button" @click="closeTailoredResumePanel" class="text-muted-foreground hover:text-foreground p-1 shrink-0 text-lg">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Copy / Download row -->
        <div class="flex items-center justify-end gap-2 px-5 py-3 border-b border-border shrink-0">
          <button
            type="button"
            @click="copyTailoredResume"
            class="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg border border-border px-3 py-1.5 hover:bg-accent transition-colors"
            :class="tailoredResumeCopied ? 'text-emerald-600 border-emerald-300' : 'text-muted-foreground'"
          >
            <i :class="tailoredResumeCopied ? 'fas fa-check' : 'far fa-copy'" class="text-[11px]"></i>
            {{ tailoredResumeCopied ? 'Copied!' : 'Copy' }}
          </button>
          <button
            v-if="tailoredResumeReplacements.length"
            type="button"
            @click="downloadTailoredResume"
            :disabled="downloadingTailoredResume"
            class="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i v-if="downloadingTailoredResume" class="fas fa-spinner fa-spin text-[11px]"></i>
            <i v-else class="fas fa-file-arrow-down text-[11px]"></i>
            {{ downloadingTailoredResume ? 'Preparing...' : 'Download Modified' }}
          </button>
        </div>

        <!-- The REAL resume file with edits applied, rendered exactly like "Your Current Resume"
             (same fonts/columns/layout). AI-changed bullets are painted with a yellow highlight;
             clicking one edits it in place, right on top of the real design. -->
        <div class="flex-1 overflow-auto bg-muted p-4">
          <div v-if="loadingTailoredResumePreview" class="flex flex-col items-center gap-2 py-10 text-muted-foreground">
            <i class="fas fa-spinner fa-spin text-lg"></i>
            <span class="text-xs">Rendering your tailored resume…</span>
          </div>
          <div v-else-if="tailoredResumePdfSource" ref="tailoredPreviewWrapperRef" class="relative bg-white shadow-sm mx-auto max-w-2xl">
            <vue-pdf-embed
              :source="tailoredResumePdfSource"
              class="w-full"
              text-layer
              @click="handleTailoredPreviewClick"
            />
            <!-- Inline edit popover, positioned right where the clicked text was -->
            <div
              v-if="tailoredResumeEditingOriginal !== null && tailoredResumeEditPopoverPos"
              data-tailored-editing
              class="absolute z-10 bg-card border border-primary/40 rounded-lg shadow-lg p-2 w-72"
              :style="{ top: tailoredResumeEditPopoverPos.top + 'px', left: tailoredResumeEditPopoverPos.left + 'px' }"
            >
              <div
                :ref="(el) => initTailoredEditableDiv(el, tailoredResumeEditingCurrentText)"
                contenteditable="true"
                class="text-xs leading-relaxed border border-border rounded px-2 py-1.5 bg-background text-foreground focus:ring-2 focus:ring-primary/30 outline-none whitespace-pre-wrap max-h-40 overflow-auto"
                @keydown.enter.exact.prevent="saveTailoredEdit($event.target.textContent)"
                @blur="saveTailoredEdit($event.target.textContent)"
                @keydown.esc="tailoredResumeEditingOriginal = null"
              ></div>
              <p class="text-[10px] text-muted-foreground mt-1">Enter to save, Esc to cancel</p>
            </div>
          </div>
          <p v-else-if="tailoredResumePreviewUnavailable" class="text-xs text-muted-foreground py-6 text-center px-4">
            Formatted preview isn't available for this file type (DOCX). Downloading still applies your edits to the real file with the design preserved.
          </p>
        </div>
      </div>
    </transition>

    <!-- Share Modal -->

    <div
      v-if="showShareModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border max-w-md w-full">
        <div class="p-6 border-b border-border">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-semibold text-foreground">Share This Job</h3>

            <button
              @click="showShareModal = false"
              class="text-muted-foreground hover:text-foreground"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="p-6">
          <div class="mb-6">
            <label class="block text-sm font-medium text-foreground mb-2"
              >Job Link</label
            >

            <div class="flex">
              <input
                type="text"
                :value="jobUrl"
                readonly
                class="flex-1 border border-r-0 border-border rounded-l-lg p-2.5 focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
              />

              <button
                @click="copyToClipboard(jobUrl)"
                class="bg-muted/50 border border-l-0 border-border rounded-r-lg px-4 hover:bg-muted/70 transition-colors"
              >
                <i class="far fa-copy"></i>
              </button>
            </div>

            <p
              v-if="copied"
              class="text-sm text-green-600 mt-1 flex items-center gap-1"
            >
              <i class="fas fa-check-circle"></i>

              <span>Copied to clipboard!</span>
            </p>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <button
              @click="shareVia('linkedin')"
              class="flex flex-col items-center justify-center p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <i class="fab fa-linkedin text-primary text-xl mb-1"></i>

              <span class="text-xs text-foreground">LinkedIn</span>
            </button>

            <button
              @click="shareVia('twitter')"
              class="flex flex-col items-center justify-center p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <i class="fab fa-twitter text-primary text-xl mb-1"></i>

              <span class="text-xs text-foreground">Twitter</span>
            </button>

            <button
              @click="shareVia('facebook')"
              class="flex flex-col items-center justify-center p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <i class="fab fa-facebook text-primary text-xl mb-1"></i>

              <span class="text-xs text-foreground">Facebook</span>
            </button>

            <button
              @click="shareVia('email')"
              class="flex flex-col items-center justify-center p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <i class="fas fa-envelope text-muted-foreground text-xl mb-1"></i>

              <span class="text-xs text-foreground">Email</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Application Error Modal -->

    <div
      v-if="showApplicationErrorModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border max-w-lg w-full max-h-[90vh] overflow-auto shadow-xl">
        <div class="p-5 border-b border-border flex justify-between items-center">
          <div class="flex items-center gap-2">
            <i class="fas fa-exclamation-circle text-red-500 text-lg"></i>
            <h3 class="text-lg font-semibold text-foreground">Application Failed</h3>
          </div>
          <button
            @click="showApplicationErrorModal = false"
            class="text-muted-foreground hover:text-foreground p-1"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="p-5">
          <p class="text-foreground mb-4">{{ job.applicationError?.message || "There was an error submitting your application." }}</p>

          <div v-if="job.applicationError?.details?.possible_causes" class="mb-4">
            <h4 class="text-sm font-semibold text-foreground mb-1">Possible causes:</h4>
            <ul class="list-disc pl-4 space-y-1 text-sm text-muted-foreground">
              <li v-for="(cause, index) in job.applicationError.details.possible_causes" :key="index">{{ cause }}</li>
            </ul>
          </div>

          <div v-if="applicationErrorOriginalJobUrl" class="mt-4 pt-4 border-t border-border">
            <p class="text-sm text-muted-foreground mb-3">
              Want to apply directly? You can open the original job post and apply from there.
            </p>
            <a
              :href="applicationErrorOriginalJobUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition font-medium"
              @click="showApplicationErrorModal = false"
            >
              <span>Go to Original Job Post</span>
              <i class="fas fa-external-link-alt text-sm"></i>
            </a>
          </div>

          <div class="mt-4 flex justify-end">
            <button
              @click="showApplicationErrorModal = false"
              class="px-4 py-2 border border-border hover:bg-muted/50 rounded-lg text-foreground text-sm transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- OnlineJobs.ph Integration Modal -->

    <div
      v-if="showIntegrationModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-foreground mb-4">
          OnlineJobs.ph Connection Required
        </h3>

        <p class="text-muted-foreground mb-6">
          To apply for jobs, you need to connect your OnlineJobs.ph account.
          Would you like to connect your account now?
        </p>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="showIntegrationModal = false"
            class="px-4 py-2 bg-muted/50 hover:bg-muted/70 text-foreground rounded-lg transition duration-200"
          >
            Cancel
          </button>

          <button
            @click="goToIntegrationSettings"
            class="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition duration-200"
          >
            Connect
          </button>
        </div>
      </div>
    </div>

    <!-- Cover letter + apply: the same side panel as Job Filters -->
    <QuickApplySidebar
      :open="coverLetterPanelOpen"
      :job="job"
      cover-letter-required-modal
      @close="coverLetterPanelOpen = false"
      @applied="onCoverLetterPanelApplied"
      @open-external="openExternalJobPost"
      @cover-letter-required="showCoverLetterRequiredModal = true"
    />

    <!-- Cover Letter Required Modal -->

    <div
      v-if="showCoverLetterRequiredModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-[10060] flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-foreground">
            Cover Letter Required
          </h3>

          <button
            @click="showCoverLetterRequiredModal = false"
            class="text-muted-foreground hover:text-foreground"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mb-6 p-4 bg-amber-500/10 border-l-4 border-amber-500 rounded">
          <p class="text-foreground">
            Please provide a cover letter for your application.
          </p>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            @click="showCoverLetterRequiredModal = false"
            class="px-4 py-2 bg-primary hover:bg-primary-dark text-primary-foreground rounded-lg transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- Cover letter feedback (after successful apply) -->

    <div
      v-if="showCoverLetterSurveyModal"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cl-survey-title"
    >
      <div class="bg-card rounded-xl border border-border shadow-xl max-w-md w-full p-6">
        <h3 id="cl-survey-title" class="text-lg font-semibold text-foreground mb-1">
          How was your cover letter?
        </h3>
        <p class="text-sm text-muted-foreground mb-4">
          You applied using
          <span class="font-medium text-foreground">{{
            coverLetterDraftSourceLabel
          }}</span
          >. Rate this draft to help us improve.
        </p>
        <div class="flex justify-center gap-2 mb-4" role="group" aria-label="Star rating 1 to 5">
          <button
            v-for="n in 5"
            :key="n"
            type="button"
            @click="surveyStars = n"
            class="text-3xl leading-none p-1 rounded transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary"
            :aria-pressed="surveyStars === n"
            :aria-label="`${n} star${n > 1 ? 's' : ''}`"
          >
            <span :class="surveyStars >= n ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/30'">★</span>
          </button>
        </div>
        <label class="block text-sm font-medium text-foreground mb-1"
          >Comment (optional)</label
        >
        <textarea
          v-model="surveyComment"
          rows="3"
          maxlength="2000"
          class="w-full border border-border rounded-lg p-2 text-sm mb-4 focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
          placeholder="What would make this cover letter better?"
        ></textarea>
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            @click="dismissCoverLetterSurvey"
            class="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted/50"
          >
            Skip
          </button>
          <button
            type="button"
            @click="submitCoverLetterSurvey"
            :disabled="surveySubmitting || !surveyStars"
            class="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ surveySubmitting ? "Sending…" : "Submit feedback" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Did you apply? Modal (shown after clicking View Job Post) -->

    <div
      v-if="showExternalApplyModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border shadow-xl max-w-sm w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
            <i class="fas fa-paper-plane text-sm"></i>
          </div>
          <div>
            <h3 class="text-base font-semibold text-foreground">Did you apply?</h3>
            <p class="text-xs text-muted-foreground mt-0.5">Track this application in your dashboard.</p>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <button
            @click="markAsExternallyApplied"
            :disabled="externalApplySubmitting"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <i v-if="externalApplySubmitting" class="fas fa-spinner fa-spin text-xs"></i>
            <i v-else class="fas fa-check text-xs"></i>
            Yes, I applied
          </button>
          <button
            @click="showExternalApplyModal = false"
            :disabled="externalApplySubmitting"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            Not yet
          </button>
        </div>
      </div>
    </div>

    <!-- Login/Register Prompt Modal -->

    <LoginRegisterPrompt
      :show="showLoginRegisterPrompt"
      @login="handleLogin"
      @register="handleRegister"
      @cancel="handleCancelPrompt"
    />

    <!-- Onboarding Required Modal -->

    <OnboardingRequiredModal
      :show="showOnboardingRequiredModal"
      @cancel="handleOnboardingCancel"
      @complete="handleOnboardingComplete"
    />

    <!-- Guided tour continuation from the Job Filters "Apply now" step -->
    <div v-if="jobDetailsTourActive" class="fixed inset-0 z-[150] pointer-events-none bg-slate-950/45"></div>
    <div
      v-if="jobDetailsTourActive"
      class="fixed inset-x-4 bottom-4 z-[220] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px]"
    >
      <div class="pointer-events-auto">
        <div class="rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
          <div class="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
            <div>
              <div class="text-[11px] font-semibold uppercase tracking-wider text-foreground">Job filter guide</div>
              <h2 class="text-base font-bold text-foreground mt-1">{{ jobDetailsCurrentTourStep.title }}</h2>
            </div>
            <button
              @click="confirmCloseJobDetailsTour"
              class="h-8 w-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition"
              aria-label="Close job filter guide"
            >
              <i class="fas fa-xmark text-sm"></i>
            </button>
          </div>
          <div class="px-5 py-4">
            <p class="text-sm text-foreground leading-relaxed">{{ jobDetailsCurrentTourStep.description }}</p>
          </div>
          <div class="px-5 py-3 bg-muted/30 border-t border-border flex items-center justify-between gap-3">
            <div class="flex items-center gap-1.5">
              <span
                v-for="(_, index) in jobDetailsTourSteps"
                :key="index"
                class="h-1.5 rounded-full transition-all"
                :class="index === jobDetailsTourStepIndex ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'"
              ></span>
            </div>
            <div class="flex items-center gap-2">
              <button
                v-if="jobDetailsTourStepIndex > 0"
                @click="previousJobDetailsTourStep"
                class="h-8 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition"
              >
                Back
              </button>
              <button
                @click="nextJobDetailsTourStep"
                class="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition"
              >
                {{ jobDetailsTourStepIndex === jobDetailsTourSteps.length - 1 ? 'Got it' : 'Next' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCloseJobDetailsTourConfirm" class="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-950/60">
      <div class="w-full max-w-sm rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
        <div class="px-5 py-4 border-b border-border">
          <h3 class="text-base font-bold text-foreground">Close this guide?</h3>
        </div>
        <div class="px-5 py-4">
          <p class="text-sm text-foreground leading-relaxed">It will be permanently marked as completed and won't show again.</p>
        </div>
        <div class="px-5 py-3 bg-muted/30 border-t border-border flex items-center justify-end gap-2">
          <button
            @click="cancelCloseJobDetailsTour"
            class="h-8 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            Cancel
          </button>
          <button
            @click="proceedCloseJobDetailsTour"
            class="h-8 px-3 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition"
          >
            Close guide
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUpgradeModal } from "@/composables/useUpgradeModal";
import { apiClient, fetchJobs } from "@/services/api";
import { OLJ_RECONNECT_ROUTE, isOljNeedsReconnect } from "@/utils/oljConnection";
import { useStore } from "vuex";
import Toast from "@/components/Toast.vue";
import { resolveEmployerLogoUrl } from "@/utils/imageUrl";
import VuePdfEmbed from "vue-pdf-embed";
import "vue-pdf-embed/dist/styles/textLayer.css";

import authStore from "@/store/auth";
import { parseAiLimitError } from "@/utils/aiLimitError";


import LoginRegisterPrompt from "@/components/LoginRegisterPrompt.vue";
import QuickApplySidebar from "@/components/QuickApplySidebar.vue";
import CollapsibleTagList from "@/components/CollapsibleTagList.vue";

import OnboardingRequiredModal from "@/components/OnboardingRequiredModal.vue";

import { updateJobMetaTags, resetMetaTags } from "@/utils/metaTags";

import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

import {
  buildJobDetailsPath,
  createJobSlug,
  extractIdFromSlug,
} from "@/utils/slug";

/** Allowed tags for OLJ-style job description HTML (br, lists, basic formatting). */
const JOB_DESC_HTML_ALLOWED_TAGS = [
  "a",
  "b",
  "blockquote",
  "br",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

/**
 * Word-level LCS diff between two strings, used to highlight exactly what the AI
 * changed in a tailored resume bullet (vs. re-rendering the whole bullet as "changed").
 * Returns an array of { text, type } where type is "same" | "removed" | "added".
 */
function diffWords(oldStr, newStr) {
  const a = (oldStr || "").split(/(\s+)/).filter(Boolean);
  const b = (newStr || "").split(/(\s+)/).filter(Boolean);
  const m = a.length;
  const n = b.length;
  const lcs = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const result = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      result.push({ text: a[i], type: "same" });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      result.push({ text: a[i], type: "removed" });
      i++;
    } else {
      result.push({ text: b[j], type: "added" });
      j++;
    }
  }
  while (i < m) result.push({ text: a[i++], type: "removed" });
  while (j < n) result.push({ text: b[j++], type: "added" });
  return result;
}

const FABRICATION_CHECK_STOPWORDS = new Set(["new", "modern", "also", "using"]);

/** Strips a trailing sentence-ending period/comma/etc. that the tokenizer regex swallows. */
function stripTrailingPunctuation(token) {
  return token.replace(/[.,;:)]+$/, "");
}

/** Lowercased, de-duplicated "words" (incl. tokens like CI/CD, C++) from a chunk of text. */
function extractWords(text) {
  const raw = (text || "").toLowerCase().match(/[a-z][a-z0-9+/#.-]{2,}/g) || [];
  return new Set(raw.map(stripTrailingPunctuation));
}

/**
 * Real tool/skill/technology names are almost always capitalized or acronym-like
 * (Playwright, TypeScript, SQL, CI/CD) — ordinary rephrasing words introduced by
 * paraphrasing (enabling, efficient, teams, define) are not. Restricting the fabrication
 * check to proper-noun/acronym-looking tokens keeps it from drowning in false positives
 * on harmless wording changes.
 */
function extractCandidateTerms(text) {
  const tokens = ((text || "").match(/[A-Za-z][A-Za-z0-9+/#.-]*/g) || []).map(stripTrailingPunctuation);
  return tokens.filter((t, i) => {
    const looksTechy = /[0-9+/#]/.test(t) || t === t.toUpperCase();
    const looksProperNoun = i > 0 && /^[A-Z]/.test(t);
    return t.length >= 3 && (looksTechy || looksProperNoun);
  });
}

/**
 * True if `keyword` (or a close inflection of it, e.g. vulnerability/vulnerabilities)
 * appears in `words`. Requires BOTH sides to be at least 5 characters before treating a
 * shared prefix as a match — otherwise a short unrelated word (e.g. "soft" from the
 * resume's own "Soft Skills" heading) can falsely "stem-match" a longer keyword
 * (e.g. "Software") that merely happens to start with the same few letters.
 */
function keywordAppearsIn(keyword, words) {
  const key = keyword.toLowerCase();
  if (key.length < 5) return words.has(key);
  const stem = key.slice(0, 5);
  for (const w of words) {
    if (w === key) return true;
    if (w.length >= 5 && w.slice(0, 5) === stem) return true;
  }
  return false;
}

/**
 * Heuristic-only check for the AI fabrication guardrail — flags two kinds of signal:
 *  1. Proper-noun/acronym-looking words (Playwright, TypeScript, SQL) in the updated bullet
 *     that don't appear ANYWHERE in the candidate's original resume.
 *  2. Any of the job's "missing keywords" (terms the relevance check already established
 *     are absent from the candidate's whole resume) showing up in this bullet — this catches
 *     domain-shifting fabrication that doesn't look like a proper noun, e.g. rephrasing plain
 *     QA testing into "security testing" / "vulnerability assessment" for a security job.
 * Not proof of fabrication, but worth a look either way.
 */
function findPossiblyFabricatedTerms(updatedText, originalResumeWords, missingKeywords) {
  const seen = new Set();
  const result = [];
  extractCandidateTerms(updatedText).forEach((t) => {
    const key = t.toLowerCase();
    if (!FABRICATION_CHECK_STOPWORDS.has(key) && !originalResumeWords.has(key) && !seen.has(key)) {
      seen.add(key);
      result.push(t);
    }
  });
  // Only check words the bullet didn't already have — a keyword stem-matching a word that
  // was already in the original resume (e.g. "Development" vs. pre-existing "developer") is
  // not new information, so it isn't fabrication even if it happens to share a stem.
  const newWords = new Set([...extractWords(updatedText)].filter((w) => !originalResumeWords.has(w)));
  (missingKeywords || []).forEach((kw) => {
    const key = kw.toLowerCase();
    if (!seen.has(key) && keywordAppearsIn(kw, newWords)) {
      seen.add(key);
      result.push(kw);
    }
  });
  return result;
}

/** Strip a leading bullet marker (•, -, etc.) off a resume line, returning { marker, indent, rest }. */
function splitBulletLine(line) {
  const match = line.match(/^(\s*)([•–\-·★◆▪○✓»❖])\s?(.*)$/);
  if (!match) return null;
  return { indent: match[1], marker: match[2], rest: match[3] };
}

const RESUME_DATE_LINE_RE = /^\d{4}\s*[·\-–]\s*(\d{4}|present)$/i;

function isResumeDateLine(text) {
  return RESUME_DATE_LINE_RE.test((text || "").trim());
}

function isResumeHeadingLine(text) {
  const trimmed = (text || "").trim();
  const letters = trimmed.replace(/[^a-zA-Z]/g, "");
  return letters.length >= 3 && letters === letters.toUpperCase() && trimmed.length <= 40;
}

/**
 * Classify a plain-text resume line so the preview can restore some visual hierarchy
 * (bold name/section headers/job titles, muted dates) that plain-text extraction loses —
 * the extracted text has no font/weight info, only the words themselves.
 */
function classifyResumeLine(rawLines, index, seenNonEmptyBefore) {
  const trimmed = (rawLines[index] || "").trim();
  if (!trimmed) return "blank";
  if (!seenNonEmptyBefore) return "name";
  if (isResumeHeadingLine(trimmed)) return "heading";
  if (isResumeDateLine(trimmed)) return "date";
  if (isResumeDateLine(rawLines[index + 1])) return "title";
  if (isResumeDateLine(rawLines[index - 1])) return "company";
  return "normal";
}

export default {
  name: "JobDetailsPage",

  components: {
    Toast,

    QuickApplySidebar,

    LoginRegisterPrompt,

    OnboardingRequiredModal,

    VuePdfEmbed,

    CollapsibleTagList,
  },

  setup() {
    const { openModal } = useUpgradeModal()

    // Initialize markdown-it for rendering job descriptions
    const md = new MarkdownIt();

    const route = useRoute();

    const router = useRouter(); // Add router for navigation

    const previousPath = window.history.state?.back || null;
    const isFromJobFilters = previousPath?.includes('/job-filters') ?? false;
    const isFromJobsListing = !isFromJobFilters && (previousPath === '/jobs' || previousPath?.startsWith('/jobs?') || previousPath === '/jobs/');
    const showBackButton = isFromJobFilters || isFromJobsListing;
    const backLabel = isFromJobFilters ? 'Job Filters' : 'Find Jobs';
    const backPath = isFromJobFilters ? '/job-filters' : '/jobs';

    const deriveJobId = () =>
      extractIdFromSlug(route.params.slug || route.params.id || "");

    const jobId = ref(deriveJobId());

    const store = useStore();

    // Toast State

    const showToast = ref(false);

    const toastType = ref("success");

    const toastTitle = ref("");

    const toastMessage = ref("");

    const logoLoadError = ref(false);

    const employerLogoUrl = computed(() => resolveEmployerLogoUrl(job.value));

    // Helper function to show toast and auto-hide after a few seconds

    const showToastNotification = (type, title, message, duration = 3000) => {
      // Reset any existing toast first

      showToast.value = false;

      // Set toast properties

      toastType.value = type;

      toastTitle.value = title;

      toastMessage.value = message;

      // Use next tick to ensure DOM updates before showing

      setTimeout(() => {
        showToast.value = true;

        // Auto-hide after duration

        setTimeout(() => {
          showToast.value = false;
        }, duration);
      }, 50);
    };


    // Polling helper for async Celery tasks


    // Job Data

    const job = ref({});

    const loading = ref(true);

    const error = ref(null);

    // Similar Jobs

    const similarJobs = ref([]);

    const loadingSimilar = ref(false);

    // User Resumes

    const userResumes = ref([]);

    // Application State



    const coverLetterDraftSource = ref("manual");
    const showCoverLetterSurveyModal = ref(false);
    const surveyCoverLetterId = ref(null);
    const surveyStars = ref(0);
    const surveyComment = ref("");
    const surveySubmitting = ref(false);



    /** Free-trial daily AI generate cap (from GET /cover-letters/ai-generate-quota/). */
    const aiGenerateQuota = ref(null);
    const aiGenerateQuotaLoading = ref(false);
















    // Share State

    const showShareModal = ref(false);

    const copied = ref(false);

    // Error handling

    const showApplicationErrorModal = ref(false);

    // Integration modal

    const showIntegrationModal = ref(false);

    // Cover letter required modal

    const showCoverLetterRequiredModal = ref(false);


    // Store current unsaved selections for immediate use


    // New state for apply points




    // Whether the logged-in user has an active OnlineJobs.ph integration.
    // The Submit button must stay hidden until this is confirmed true —
    // otherwise users without a connected OLJ account could submit an
    // application that only gets rejected after the fact by the backend.
    const oljConnected = ref(false);
    // OLJ rejected the saved login: applying and apply points won't work until reconnect.
    const oljNeedsReconnect = ref(false);



    // Modal state

    const showLoginRegisterPrompt = ref(false);

    const showOnboardingRequiredModal = ref(false);

    // Removed subscription expiration checks - all users have Free plan

    // Computed Properties

    const employerInitials = computed(() => {
      if (!job.value.employer?.name) return "CO";
      return job.value.employer.name

        .split(" ")

        .map((word) => word[0])

        .join("")

        .substring(0, 2)

        .toUpperCase();
    });

    const isSaved = computed(() => {
      return store.getters["savedJobs/isJobSaved"](jobId.value);
    });

    /** OLJ apply error modal: API may send details.job_url; else use job.link. */
    const applicationErrorOriginalJobUrl = computed(() => {
      const d = job.value?.applicationError?.details;
      const fromApi = d?.job_url;
      if (fromApi != null && String(fromApi).trim() !== "") {
        return String(fromApi).trim();
      }
      const link = job.value?.link;
      if (link != null && String(link).trim() !== "") {
        return String(link).trim();
      }
      return "";
    });

    const canUseAICoverLetterFeatures = computed(() => {
      if (!authStore.state.isLoggedIn) return false;
      // Free plan users can generate letters up to their monthly quota.
      return true;
    });

    const isSubscriptionExpired = computed(() => {
      if (!authStore.state.isLoggedIn) return false;
      const user = authStore.state.user;
      const planType = user?.subscription?.plan?.plan_type;
      const subStatus = user?.subscription?.status;
      return (planType === "basic" || planType === "premium") && subStatus === "expired";
    });

    const isSubscribedUser = computed(() => {
      if (!authStore.state.isLoggedIn) return false;
      const user = authStore.state.user;
      const planType = user?.subscription?.plan?.plan_type;
      const subStatus = user?.subscription?.status;
      return (planType === "basic" || planType === "premium") && subStatus === "active";
    });

    const isFreeTrialAiLimited = computed(
      () => aiGenerateQuota.value?.applies === true,
    );

    const trialAiQuotaRemaining = computed(() =>
      aiGenerateQuota.value?.applies === true
        ? aiGenerateQuota.value?.remaining
        : null,
    );

    const trialAiQuotaLimit = computed(() =>
      aiGenerateQuota.value?.applies === true
        ? aiGenerateQuota.value?.limit
        : null,
    );


    const canGenerateAiCoverLetterNow = computed(() => {
      if (!canUseAICoverLetterFeatures.value) return false;
      const q = aiGenerateQuota.value;
      if (q == null || !q.applies) return true;
      return (q.remaining ?? 0) > 0;
    });

    const generateWithAiButtonTitle = computed(() => {
      if (!authStore.state.isLoggedIn) {
        return "Sign in to generate a cover letter with AI";
      }
      if (!canUseAICoverLetterFeatures.value) {
        return "Upgrade to Basic or Premium to use AI cover letters";
      }
      const q = aiGenerateQuota.value;
      if (aiGenerateQuotaLoading.value || q == null) {
        return "Loading AI cover letter quota…";
      }
      if (!q.applies) {
        return "Unlimited AI cover letters on your plan";
      }
      const rem = q.remaining ?? 0;
      const lim = q.limit ?? 10;
      if (rem <= 0) {
        return `Monthly limit reached — ${lim} AI cover letters used. Resets on the 1st of next month.`;
      }
      return `${rem} of ${lim} AI cover letters remaining this month`;
    });

    const showAiCoverLetterUpgradeHint = computed(
      () =>
        authStore.state.isLoggedIn && !canUseAICoverLetterFeatures.value,
    );

    const expiredPlanLabel = computed(() => {
      const planType = authStore.state.user?.subscription?.plan?.plan_type;
      if (planType === 'basic') return 'Basic plan';
      if (planType === 'premium') return 'Premium plan';
      return 'Free plan';
    });

    const coverLetterDraftSourceLabel = computed(() => {
      const s = coverLetterDraftSource.value;
      if (s === "ai") return "Generate with AI";
      if (s === "quick_cover") return "Not AI generated";
      return "your own text";
    });

    const isLoggedIn = computed(() => authStore.state.isLoggedIn);

    // OnlineJobs.ph apply flow is only offered to users an admin has granted
    // OLJ access (olj_jobs_enabled); everyone else just gets "View Job Post".
    const oljUnlocked = computed(() => !!authStore.state.user?.olj_jobs_enabled);

    const isAdminUser = computed(() => {
      const user = authStore.state.user;
      if (!user) return false;
      return Boolean(
        user.is_superuser ||
          user.account_type === "admin" ||
          user.admin_profile,
      );
    });

    // The job's own site, as the apply button names it ("Apply on Upwork").
    const siteName = computed(() => {
      const src = (job.value?.source || "").toLowerCase();
      const map = {
        olj: "OnlineJobs.ph", jobstreet: "JobStreet", upwork: "Upwork", cloudstaff: "Cloudstaff", emapta: "Emapta",
        bossjob: "BossJob.ph", kalibrr: "Kalibrr", findtalent: "FindTalent.ph", hiretalent: "HireTalent.ph",
        ofmjobs: "OFM Jobs", linkedin: "LinkedIn", indeed: "Indeed", google: "Google Jobs",
      };
      return map[src] || "";
    });

    const externalPlatformLabel = computed(() => siteName.value || "Job Post");

    const externalPlatformLogo = computed(() => {
      const src = (job.value?.source || "").toLowerCase();
      const map = {
        olj: "/images/job-source-logos/olj-logo-blue.webp",
        jobstreet: "/images/job-source-logos/JOBSTREET_small_scale.png",
      };
      return map[src] || null;
    });

    const isExternalJob = computed(() => {
      const src = (job.value?.source || "").toLowerCase();
      return src !== "olj";
    });

    // Submitting from Applypa needs a connected OnlineJobs.ph account; every other job,
    // OnlineJobs.ph included, is applied for on its own site with the Chrome extension,
    // like Upwork and JobStreet (Oct 26: new users go to the extension).
    const showApplyOnSite = computed(
      () =>
        authStore.state.isLoggedIn &&
        !!job.value?.link &&
        (isExternalJob.value || !oljUnlocked.value || !oljConnected.value),
    );

    const formattedDescription = computed(() => {
      const rawHtml = job.value.description_html;
      if (typeof rawHtml === "string" && rawHtml.trim()) {
        return DOMPurify.sanitize(rawHtml.trim(), {
          ALLOWED_TAGS: JOB_DESC_HTML_ALLOWED_TAGS,
          ALLOWED_ATTR: ["href", "title", "target", "rel", "class"],
        });
      }
      if (!job.value.description) return "";
      return md.render(job.value.description);
    });

    const jobSlug = computed(() =>
      createJobSlug(job.value?.title, jobId.value),
    );

    const jobUrl = computed(() => {
      const slug = jobSlug.value || jobId.value || "";

      return `${window.location.origin}/jobs/${slug}`;
    });

    // Load Job Data

    const fetchJobDetails = async () => {
      const currentJobId = jobId.value;

      if (!currentJobId) {
        error.value = "Invalid job identifier.";

        loading.value = false;

        return;
      }

      loading.value = true;

      error.value = null;

      try {
        const response = await apiClient.get(`/jobs/${currentJobId}/`);
        job.value = response.data;

        if (route.query && route.query.apply === "1") {
          handleApplyClick();
          const nextQuery = { ...route.query };
          delete nextQuery.apply;
          router.replace({ query: nextQuery });
        }

        // Map userHasApplied to applied for UI consistency

        if ("userHasApplied" in job.value) {
          job.value.applied = job.value.userHasApplied;
        }

        const expectedSlug = createJobSlug(job.value.title, currentJobId);

        if (expectedSlug && route.params.slug !== expectedSlug) {
          router.replace({
            name: "JobDetails",

            params: { slug: expectedSlug },

            query: route.query,

            hash: route.hash,
          });
        }

        // Update Open Graph meta tags for social media sharing

        updateJobMetaTags(job.value, currentJobId);

        // After loading job, fetch similar jobs

        fetchSimilarJobs();
      } catch (err) {
        console.error("Error fetching job details:", err);

        // Handle different error types

        if (err.response?.status === 404) {
          error.value =
            "Job not found. This job may have been removed or archived.";
        } else if (err.response?.status === 403) {
          error.value = "You don't have permission to view this job.";
        } else if (err.response?.status >= 500) {
          error.value = "Server error. Please try again later.";
        } else {
          error.value = "Failed to load job details. Please try again.";
        }

        // Don't use sample data - show error instead

        job.value = {};

        // Show error toast

        showToastNotification("error", "Error Loading Job", error.value);
      } finally {
        loading.value = false;
      }
    };

    // Load Similar Jobs

    const fetchSimilarJobs = async () => {
      const currentJobId = jobId.value;

      if (!currentJobId) {
        similarJobs.value = [];

        return;
      }

      loadingSimilar.value = true;

      try {
        // First try the direct similar jobs endpoint

        const response = await apiClient.get(`/jobs/${currentJobId}/similar/`);

        let similarJobsResults = response.data || [];

        // If we don't have enough direct matches, try to find more

        if (similarJobsResults.length < 3 && job.value) {
          // First try with matching tags

          if (job.value.tags && job.value.tags.length > 0) {
            const tagIds = job.value.tags.map((tag) => tag.id).join(",");

            const tagBasedResponse = await apiClient.get(`/jobs/`, {
              params: { tags: tagIds },
            });

            // Filter out the current job and already found similar jobs

            const additionalTagJobs = (
              tagBasedResponse.data.results ||
              tagBasedResponse.data ||
              []
            ).filter(
              (j) =>
                j.id !== parseInt(currentJobId, 10) &&
                !similarJobsResults.some((sj) => sj.id === j.id),
            );

            // Calculate similarity scores

            additionalTagJobs.forEach((j) => {
              j.similarityScore = calculateSimilarityScore(j, job.value);
            });

            // Sort by similarity score and take the most relevant ones

            additionalTagJobs.sort(
              (a, b) => b.similarityScore - a.similarityScore,
            );

            // Combine with existing results, limit to 3 for the preview

            similarJobsResults = [
              ...similarJobsResults,

              ...additionalTagJobs,
            ].slice(0, 3);
          }

          // If still not enough, try with job type

          if (similarJobsResults.length < 3 && job.value.job_type) {
            const jobTypeResponse = await apiClient.get(`/jobs/`, {
              params: { job_type: job.value.job_type },
            });

            // Filter out the current job and already found similar jobs

            const jobTypeJobs = (
              jobTypeResponse.data.results ||
              jobTypeResponse.data ||
              []
            ).filter(
              (j) =>
                j.id !== parseInt(currentJobId, 10) &&
                !similarJobsResults.some((sj) => sj.id === j.id),
            );

            // Calculate similarity scores

            jobTypeJobs.forEach((j) => {
              j.similarityScore = calculateSimilarityScore(j, job.value);
            });

            // Sort by similarity and take the best matches

            jobTypeJobs.sort((a, b) => b.similarityScore - a.similarityScore);

            // Add to results, maintaining 3 job limit for the preview

            similarJobsResults = [...similarJobsResults, ...jobTypeJobs].slice(
              0,

              3,
            );
          }
        }

        // Set the results

        similarJobs.value = similarJobsResults;
      } catch (err) {
        console.error("Error fetching similar jobs:", err);

        // Don't use sample data - just show empty list

        similarJobs.value = [];
      } finally {
        loadingSimilar.value = false;
      }
    };

    watch(
      () => route.params.slug,

      (newSlug) => {
        const derivedId = extractIdFromSlug(newSlug || "");

        if (derivedId && derivedId !== jobId.value) {
          jobId.value = derivedId;

          fetchJobDetails();
        }
      },
    );

    // Calculate similarity score between two jobs

    const calculateSimilarityScore = (job, referenceJob) => {
      let score = 0;

      // Title similarity

      if (job.title && referenceJob.title) {
        const titleWords = referenceJob.title.toLowerCase().split(/\s+/);

        const jobTitleLower = job.title.toLowerCase();

        titleWords.forEach((word) => {
          if (word.length > 3 && jobTitleLower.includes(word)) {
            score += 3;
          }
        });

        // Extra bonus for exact title match or very close match

        if (jobTitleLower === referenceJob.title.toLowerCase()) {
          score += 10; // Exact match
        } else if (
          jobTitleLower.includes(referenceJob.title.toLowerCase()) ||
          referenceJob.title.toLowerCase().includes(jobTitleLower)
        ) {
          score += 5; // One title contains the other
        }
      }

      // Tag matches

      const refTagIds = (referenceJob.tags || []).map((tag) => tag.id);

      const jobTagIds = (job.tags || []).map((tag) => tag.id);

      jobTagIds.forEach((tagId) => {
        if (refTagIds.includes(tagId)) {
          score += 2;
        }
      });

      // Job type match

      if (job.job_type === referenceJob.job_type) {
        score += 2;
      }

      // Employer match (might be same company, different role)

      if (job.employer?.id === referenceJob.employer?.id) {
        score += 3;
      }

      return score;
    };

    const getJobDetailPath = (jobItem) =>
      buildJobDetailsPath(jobItem?.title, jobItem?.id);

    // Load User Resumes

    const fetchUserResumes = async () => {
      try {
        const response = await apiClient.get("/resumes/");
        userResumes.value = response.data.results || [];
      } catch (err) {
        console.error("Error fetching user resumes:", err);
        userResumes.value = [];
      }
    };

    // Generate Cover Letter with AI

    // Modify the generateCoverLetter function to handle null values

    // First, add a new state variable for the AI generation loading



    const fetchAiGenerateQuota = async () => {
      if (!authStore.state.isLoggedIn) {
        aiGenerateQuota.value = null;
        return;
      }
      aiGenerateQuotaLoading.value = true;
      try {
        const res = await apiClient.get("/cover-letters/ai-generate-quota/");
        aiGenerateQuota.value = res.data || null;
      } catch (e) {
        aiGenerateQuota.value = null;
      } finally {
        aiGenerateQuotaLoading.value = false;
      }
    };

    // Then update the generateCoverLetter function to use this state

    // Guided tour that continues from the JobFiltersPage "Apply now" tour
    // step. For OLJ jobs it first explains where to apply (on OnlineJobs.ph with
    // the extension, or in-app with a connected account), then points new users
    // at the AI cover letter generator.
    const jobDetailsTourActive = computed(() => route.query.tour === "2");
    const initialJobDetailsTourStep = parseInt(route.query.step, 10);
    const jobDetailsTourStepIndex = ref(
      Number.isInteger(initialJobDetailsTourStep) && initialJobDetailsTourStep >= 0
        ? initialJobDetailsTourStep
        : 0
    );

    const jobDetailsTourSteps = computed(() => {
      const steps = [];
      if (!isExternalJob.value && oljUnlocked.value) {
        steps.push({
          key: "olj",
          title: oljConnected.value ? "Connect OnlineJobs.ph" : "Apply on OnlineJobs.ph",
          description: oljConnected.value
            ? "Your OnlineJobs.ph account is already connected, so you can submit this application directly from Applypa."
            : "This job is posted on OnlineJobs.ph. Tap \"Apply on OnlineJobs.ph\" to open it there - the Applypa Chrome extension writes your cover letter and fills in the application on the job page.",
        });
      }
      steps.push({
        key: "generate",
        title: "Generate with AI",
        description: "Let AI write a tailored cover letter for this job in seconds. Click the button above to try it.",
      });
      return steps;
    });

    const jobDetailsCurrentTourStep = computed(
      () => jobDetailsTourSteps.value[jobDetailsTourStepIndex.value] || jobDetailsTourSteps.value[0]
    );

    const jobDetailsTourHighlightClass = (key) => {
      if (!jobDetailsTourActive.value || jobDetailsCurrentTourStep.value.key !== key) return "";
      return "relative z-[170] ring-2 ring-primary ring-offset-4 ring-offset-background shadow-2xl";
    };

    const syncJobDetailsTourStepQuery = () => {
      if (route.query.tour !== "2") return;
      const nextQuery = { ...route.query, step: String(jobDetailsTourStepIndex.value) };
      router.replace({ query: nextQuery }).catch(() => {});
    };

    const nextJobDetailsTourStep = () => {
      if (jobDetailsTourStepIndex.value >= jobDetailsTourSteps.value.length - 1) {
        finishJobDetailsTour();
        return;
      }
      jobDetailsTourStepIndex.value += 1;
      syncJobDetailsTourStepQuery();
    };

    const previousJobDetailsTourStep = () => {
      if (jobDetailsTourStepIndex.value > 0) jobDetailsTourStepIndex.value -= 1;
      syncJobDetailsTourStepQuery();
    };

    function finishJobDetailsTour() {
      jobDetailsTourStepIndex.value = 0;
      if (route.query.tour === undefined) return;
      const nextQuery = { ...route.query };
      delete nextQuery.tour;
      delete nextQuery.step;
      router.replace({ query: nextQuery }).catch(() => {});
    }

    const showCloseJobDetailsTourConfirm = ref(false);

    const confirmCloseJobDetailsTour = () => {
      showCloseJobDetailsTourConfirm.value = true;
    };

    const cancelCloseJobDetailsTour = () => {
      showCloseJobDetailsTourConfirm.value = false;
    };

    const proceedCloseJobDetailsTour = () => {
      showCloseJobDetailsTourConfirm.value = false;
      finishJobDetailsTour();
    };




    const coverLetterPanelOpen = ref(false);

    const finishApplySuccessUi = (coverLetterId, successMessage) => {
      job.value.applied = true;
      if (Object.prototype.hasOwnProperty.call(job.value, "userHasApplied")) {
        job.value.userHasApplied = true;
      }
      showToastNotification(
        "success",
        "Application Submitted",
        successMessage || "Your application has been submitted successfully!",
      );
      if (coverLetterId != null) {
        surveyCoverLetterId.value = coverLetterId;
        surveyStars.value = 0;
        surveyComment.value = "";
        showCoverLetterSurveyModal.value = true;
      }
    };

    // Submit Application



    // Save Job

    const saveJob = async () => {
      if (!authStore.state.isLoggedIn) {
        showLoginRegisterPrompt.value = true;

        return;
      }

      // Removed subscription expiration check - all users have Free plan

      try {
        // Capture current state before toggling

        const wasCurrentlySaved = isSaved.value;


        // Use the toggleSaveJob action which handles both saving and unsaving

        const result = await store.dispatch(
          "savedJobs/toggleSaveJob",

          job.value,
        );

        if (result.success) {
          // Force refresh all saved jobs to ensure consistent state

          await store.dispatch("savedJobs/fetchSavedJobs");

          // Check the current status after refreshing



          // Update local state to match store

          // Show appropriate toast notification

          if (wasCurrentlySaved) {
            // If it was saved before, we've removed it

            showToastNotification(
              "success",

              "Job Removed",

              "The job has been removed from your saved jobs.",
            );
          } else {
            // If it wasn't saved before, we've added it

            showToastNotification(
              "success",

              "Job Saved",

              "The job has been added to your saved jobs.",
            );
          }
        }
      } catch (error) {
        console.error("Error saving/unsaving job:", error);

        // Show error toast

        showToastNotification(
          "error",

          "Error",

          "An error occurred. Please try again.",
        );
      }
    };

    // Share Job

    const shareJob = () => {
      showShareModal.value = true;
    };

    // Copy to Clipboard

    const copyToClipboard = (text) => {
      navigator.clipboard

        .writeText(text)

        .then(() => {
          copied.value = true;

          setTimeout(() => {
            copied.value = false;
          }, 3000);
        })

        .catch((err) => {
          console.error("Could not copy text: ", err);
        });
    };

    // Share via different platforms

    const shareVia = (platform) => {
      const url = encodeURIComponent(jobUrl.value);

      const title = encodeURIComponent(
        `Check out this job: ${job.value.title}`,
      );

      let shareUrl = "";

      switch (platform) {
        case "linkedin":
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;

          break;

        case "twitter":
          shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;

          break;

        case "facebook":
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;

          break;

        case "email":
          shareUrl = `mailto:?subject=${title}&body=I thought you might be interested in this job: ${jobUrl.value}`;

          break;
      }

      window.open(shareUrl, "_blank");
    };

    // Format Date

    // Update the formatDate function to handle potentially invalid dates

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown date";

      try {
        const date = new Date(dateString);

        // Check if date is valid

        if (isNaN(date.getTime())) {
          return "Invalid date";
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",

          month: "long",

          day: "2-digit",

          timeZone: "Asia/Manila",
        });
      } catch (error) {
        console.error("Error formatting date:", error);

        return "Invalid date";
      }
    };

    const formatPostedDateTime = (dateString) => {
      if (!dateString) return "Unknown date";

      try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
          return "Invalid date";
        }

        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("Error formatting date/time:", error);
        return "Invalid date";
      }
    };

    const fetchAllJobs = async () => {
      try {
        await fetchJobs();


      } catch (error) {
        console.error("Failed to fetch jobs", error);
      }
    };

    // Fetch whether the user has an active OnlineJobs.ph integration
    const fetchOljConnectionStatus = async () => {
      if (!authStore.state.isLoggedIn) {
        oljConnected.value = false;
        return;
      }

      try {
        const res = await apiClient.get("/dashboard/summary/");
        oljConnected.value = !!res.data?.olj_connected;
        oljNeedsReconnect.value = isOljNeedsReconnect(res.data?.olj_connection_status);
      } catch (err) {
        console.error("Error checking OLJ connection status:", err);
        oljConnected.value = false;
        oljNeedsReconnect.value = false;
      }
    };


    // Load Data on Mount

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });

      fetchAllJobs();

      fetchJobDetails();

      fetchUserResumes();

      // Only fetch saved jobs if user is logged in

      if (authStore.state.isLoggedIn) {
        store.dispatch("savedJobs/fetchSavedJobs");
      } else {
        // Clear saved jobs when not logged in

        store.commit("savedJobs/SET_SAVED_JOBS", []);
      }

      if (authStore.state.isLoggedIn) {
        fetchAiGenerateQuota();
      }

      fetchOljConnectionStatus();
    });

    // Reset meta tags when component is unmounted

    onBeforeUnmount(() => {
      resetMetaTags();
    });

    // Navigate to integration settings

    const goToIntegrationSettings = () => {
      showIntegrationModal.value = false;
      router.push({ path: "/settings", query: { section: "integrations" } });
    };

    // Helper function to extract prompt string from saved instructions

    // Handles multiple formats: new instructions library, old structured format, plain string


    // AI Instructions

    const formatQuickCoverErrorMessage = (err) => {
      const data = err?.response?.data;
      const status = err?.response?.status;
      if (typeof data?.error === "string" && data.error.trim()) {
        return data.error.trim();
      }
      if (typeof data?.detail === "string" && data.detail.trim()) {
        return data.detail.trim();
      }
      if (Array.isArray(data?.detail) && data.detail.length) {
        return data.detail
          .map((x) =>
            typeof x === "string" ? x : x?.string || JSON.stringify(x),
          )
          .join(" ");
      }
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const parts = Object.entries(data)
          .map(([k, v]) => {
            if (Array.isArray(v)) return `${k}: ${v.join(", ")}`;
            if (v != null && typeof v === "object") return `${k}: ${JSON.stringify(v)}`;
            return `${k}: ${v}`;
          })
          .filter(Boolean);
        if (parts.length) return parts.join("; ");
      }
      if (err?.code === "ECONNABORTED") {
        return "Request timed out. Check your connection and try again.";
      }
      const em = typeof err?.message === "string" ? err.message : "";
      if (/timeout|TIMEOUT/i.test(em)) {
        return "Request timed out. Check your connection and try again.";
      }
      if (status) {
        return `Quick cover failed (HTTP ${status}).`;
      }
      if (em) return em;
      return "Network error or server unreachable. Check your connection and try again.";
    };

    const dismissCoverLetterSurvey = () => {
      showCoverLetterSurveyModal.value = false;
      surveyCoverLetterId.value = null;
      surveyStars.value = 0;
      surveyComment.value = "";
    };

    const submitCoverLetterSurvey = async () => {
      if (!surveyCoverLetterId.value) {
        dismissCoverLetterSurvey();
        return;
      }
      if (!surveyStars.value || surveyStars.value < 1) {
        showToastNotification(
          "warning",
          "Rating",
          "Choose 1–5 stars to submit, or tap Skip.",
          4000,
        );
        return;
      }
      surveySubmitting.value = true;
      try {
        await apiClient.post("/cover-letters/feedback/", {
          cover_letter_id: surveyCoverLetterId.value,
          stars: surveyStars.value,
          comment: surveyComment.value.trim() || undefined,
        });
        showToastNotification(
          "success",
          "Thank you",
          "Your feedback helps us improve cover letters.",
          4000,
        );
        dismissCoverLetterSurvey();
      } catch (err) {
        showToastNotification(
          "warning",
          "Feedback",
          formatQuickCoverErrorMessage(err),
          6000,
        );
      } finally {
        surveySubmitting.value = false;
      }
    };


    const autoResizeTailoredResumeTextarea = async () => {
      await nextTick();
      requestAnimationFrame(() => {
        const el = tailoredResumeTextareaRef.value;
        if (!el) return;
        const minPx = 260;
        const clone = el.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.height = 'auto';
        clone.style.width = el.offsetWidth + 'px';
        clone.style.overflow = 'hidden';
        document.body.appendChild(clone);
        const newHeight = Math.max(minPx, clone.scrollHeight);
        document.body.removeChild(clone);
        el.style.height = `${newHeight}px`;
      });
    };

    const generateTailoredResume = async (forceLowRelevance = false) => {
      if (!authStore.state.isLoggedIn) {
        showLoginRegisterPrompt.value = true;
        return;
      }
      if (!canUseAICoverLetterFeatures.value) {
        showToastNotification(
          "warning",
          "Upgrade required",
          "Tailor Resume with AI is available on Basic and Premium plans.",
          4000,
        );
        return;
      }
      if (!canGenerateAiCoverLetterNow.value) {
        const lim =
          aiGenerateQuota.value?.applies === true &&
          typeof aiGenerateQuota.value?.limit === "number"
            ? aiGenerateQuota.value.limit
            : 10;
        showToastNotification(
          "warning",
          "Monthly limit reached",
          `Your plan includes ${lim} AI generations per month. Resets on the 1st of next month.`,
          6000,
        );
        return;
      }
      generatingTailoredResume.value = true;
      try {
        const response = await apiClient.post(
          "/cover-letters/tailor-resume/",
          {
            job_id: jobId.value,
            resume_id: userResumes.value?.[0]?.id || null,
            use_profile: true,
            confirm_low_relevance: forceLowRelevance,
          },
          { timeout: 180000 },
        );

        if (response.data.needs_confirmation) {
          const { score, missing_keywords: missing } = response.data.relevance || {};
          generatingTailoredResume.value = false;
          const proceed = window.confirm(
            `Your resume only overlaps about ${score}% with this job` +
              (missing?.length ? ` (missing: ${missing.join(", ")}).` : ".") +
              `\n\nTailoring it anyway may produce a weak or misleading match. Generate anyway?`,
          );
          if (proceed) {
            await generateTailoredResume(true);
          }
          return;
        }

        tailoredResumeContent.value = response.data.content || "";
        tailoredResumeReplacements.value = response.data.replacements || [];
        tailoredResumeChanges.value = response.data.changes || [];
        tailoredResumeRelevance.value = response.data.relevance || null;
        tailoredResumeEditingOriginal.value = null;

        if (tailoredResumeChanges.value.length) {
          // Reconstruct the full ORIGINAL resume's word set (pre-edit text for changed lines,
          // untouched raw text for everything else) so we can flag words the AI introduced
          // that don't appear anywhere in the candidate's real resume — a check on whether
          // the "don't invent skills/tools" guardrail actually held.
          const originalResumeWords = new Set();
          tailoredResumeLines.value.forEach((line) => {
            extractWords(line.change ? line.change.original : line.raw).forEach((w) => originalResumeWords.add(w));
          });
          tailoredResumeChanges.value.forEach((c) => {
            c.suspiciousTerms = findPossiblyFabricatedTerms(
              c.updated,
              originalResumeWords,
              tailoredResumeRelevance.value?.missing_keywords,
            ); // surfaced as a warning icon on the bullet in the preview
          });
        }
        tailoredResumePanelOpen.value = true;
        showToastNotification("success", "Resume tailored", "Your tailored resume is ready — edit directly on the preview.", 4000);
        await nextTick();
        autoResizeTailoredResumeTextarea();
        fetchAiGenerateQuota();
        window.dispatchEvent(new CustomEvent("ai-quota-updated"));
      } catch (err) {
        const aiLimit = parseAiLimitError(err);
        if (aiLimit) {
          fetchAiGenerateQuota();
          showToastNotification(
            "error",
            "AI limit reached",
            aiLimit.code === "monthly_ai_limit"
              ? "You've used all your AI credits for this period. Upgrade your plan for more."
              : aiLimit.message,
            8000,
          );
          return;
        }
        if (err?.response?.status === 429) {
          fetchAiGenerateQuota();
          openModal('ai-cover-letter');
          return;
        }
        const data = err?.response?.data;
        const msg =
          (typeof data === "object" && data && (data.error || data.detail)) ||
          (typeof data === "string" ? data : null) ||
          err?.message ||
          "Could not tailor resume. Try again.";
        showToastNotification("error", "Resume tailor failed", msg, 8000);
      } finally {
        generatingTailoredResume.value = false;
      }
    };

    const copyTailoredResume = () => {
      if (!tailoredResumeContent.value) return;
      navigator.clipboard.writeText(tailoredResumeContent.value).then(() => {
        tailoredResumeCopied.value = true;
        setTimeout(() => { tailoredResumeCopied.value = false; }, 2000);
      });
    };

    const downloadTailoredResume = async () => {
      const resume = userResumes.value?.[0];
      if (!resume?.id || !tailoredResumeReplacements.value.length) return;
      downloadingTailoredResume.value = true;
      try {
        const res = await apiClient.post(
          "/cover-letters/tailor-resume-download/",
          { resume_id: resume.id, replacements: tailoredResumeReplacements.value },
          { responseType: "blob" },
        );
        const disposition = res.headers["content-disposition"] || "";
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : "tailored_resume.pdf";
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        showToastNotification("error", "Download failed", "Could not generate the modified file. Try again.", 5000);
      } finally {
        downloadingTailoredResume.value = false;
      }
    };

    const fetchResumePdf = async () => {
      const resume = userResumes.value?.[0];
      if (!resume?.id || resumePdfSource.value || loadingResumePdf.value) return;
      loadingResumePdf.value = true;
      try {
        const res = await apiClient.get(`/resumes/${resume.id}/download/`, {
          responseType: "arraybuffer",
        });
        resumePdfSource.value = { data: res.data };
      } catch (e) {
        console.error("Failed to load resume PDF:", e);
      } finally {
        loadingResumePdf.value = false;
      }
    };


    // Navigate to employer profile page

    const viewEmployerProfile = (employerId) => {
      router.push(`/employers/${employerId}`);
    };

    // Modified Apply button handler

    const handleApplyClick = () => {
      if (!authStore.state.isLoggedIn) {
        showLoginRegisterPrompt.value = true;

        return;
      }

      coverLetterPanelOpen.value = true;
    };

    const coverLetterButtonLabel = computed(() => {
      if (!authStore.state.isLoggedIn) return "Sign in to write a cover letter";
      if (!isExternalJob.value && oljUnlocked.value && oljConnected.value && !oljNeedsReconnect.value) {
        return "Write cover letter & apply";
      }
      return "Write cover letter";
    });

    // The panel submitted the application: close it, then thank and ask for feedback.
    const onCoverLetterPanelApplied = (_jobId, { coverLetterId = null, draftSource = "manual" } = {}) => {
      coverLetterPanelOpen.value = false;
      coverLetterDraftSource.value = draftSource;
      finishApplySuccessUi(coverLetterId);
    };

    // Modal event handlers

    const handleLogin = () => {
      showLoginRegisterPrompt.value = false;

      router.push({ path: "/login", query: { redirect: route.fullPath } });
    };

    const handleRegister = () => {
      showLoginRegisterPrompt.value = false;

      router.push({ path: "/register", query: { redirect: route.fullPath } });
    };

    const handleCancelPrompt = () => {
      showLoginRegisterPrompt.value = false;
    };

    // Onboarding modal event handlers

    const handleOnboardingCancel = () => {
      showOnboardingRequiredModal.value = false;
    };

    const handleOnboardingComplete = () => {
      showOnboardingRequiredModal.value = false;

      // Allow entering onboarding only via this intentional action.

      sessionStorage.setItem("onboarding_nav_grant", "1");

      let token = "";

      try {
        const bytes = new Uint8Array(16);

        (window.crypto || crypto).getRandomValues(bytes);

        token = Array.from(bytes)

          .map((b) => b.toString(16).padStart(2, "0"))

          .join("");
      } catch (e) {
        token = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      sessionStorage.setItem("onboarding_nav_token", token);

      router.push({ path: "/onboarding", query: { t: token } });
    };

    const showExternalApplyModal = ref(false);
    const externalApplySubmitting = ref(false);

    // Resume tailor state
    const generatingTailoredResume = ref(false);
    const tailoredResumeContent = ref("");
    const tailoredResumeReplacements = ref([]);
    const tailoredResumeChanges = ref([]); // [{ id, original, updated, suspiciousTerms }] — bullets the AI actually edited
    const tailoredResumeRelevance = ref(null); // { score, missing_keywords } from the pre-generate relevance check
    const relevanceBadgeClass = computed(() => {
      const score = tailoredResumeRelevance.value?.score;
      if (score == null) return "";
      if (score >= 60) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30";
      if (score >= 30) return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30";
      return "bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30";
    });
    const tailoredResumeCopied = ref(false);
    const tailoredResumeTextareaRef = ref(null);
    const downloadingTailoredResume = ref(false);

    // Formatted preview: the tailored text rendered into the user's REAL resume file
    // (same code path as "Download Modified"), so what's shown here always matches the
    // real design/layout instead of a plain-text reconstruction. Shown inside a slide-out
    // panel that opens once generation finishes.
    const tailoredResumePanelOpen = ref(false);
    const tailoredResumePdfSource = ref(null);
    const loadingTailoredResumePreview = ref(false);
    const tailoredResumePreviewUnavailable = ref(false); // non-PDF resume (DOCX) — no formatted preview possible

    const tailoredPreviewWrapperRef = ref(null);
    const tailoredResumeEditPopoverPos = ref(null);
    const tailoredResumeEditingOriginal = ref(null); // TRUE original PDF text of the bullet being edited (or null)
    const tailoredResumeEditingCurrentText = ref(""); // current value shown in the edit popover

    const fetchTailoredResumePreview = async () => {
      const resume = userResumes.value?.[0];
      if (!resume?.id || !tailoredResumeReplacements.value.length) return;
      loadingTailoredResumePreview.value = true;
      try {
        const res = await apiClient.post(
          "/cover-letters/tailor-resume-preview/",
          { resume_id: resume.id, replacements: tailoredResumeReplacements.value },
          { responseType: "arraybuffer" },
        );
        tailoredResumePdfSource.value = { data: res.data };
        tailoredResumePreviewUnavailable.value = false;
        // The backend also returns per-bullet highlight boxes via a response header, used
        // only to bake the yellow "AI-changed" highlight into the rendered PDF itself —
        // click handling below works directly off the PDF's own text layer instead, so
        // there's nothing to do with that header on the frontend.
      } catch (e) {
        tailoredResumePdfSource.value = null;
        tailoredResumePreviewUnavailable.value = true;
        console.error("Formatted tailored resume preview failed:", e);
      } finally {
        loadingTailoredResumePreview.value = false;
      }
    };

    const openTailoredResumePanel = () => {
      tailoredResumePanelOpen.value = true;
    };

    const closeTailoredResumePanel = () => {
      tailoredResumePanelOpen.value = false;
    };

    const normalizeForMatch = (text) => (text || "").toLowerCase().replace(/\s+/g, " ").trim();

    // For every bullet, the TRUE original PDF text (the key apply_replacements matches
    // against) paired with whatever text is actually rendered right now — either a
    // pending hand-edit, the AI's rewrite, or (if untouched) the original itself. Used to
    // match a click on the rendered PDF back to a specific bullet regardless of how many
    // times it's been edited this session.
    const tailoredResumeBulletIndex = computed(() =>
      tailoredResumeLines.value
        .filter((line) => line.kind === "bullet" && line.text)
        .map((line) => {
          const trueOriginal = line.change ? line.change.original : line.text;
          const existing = tailoredResumeReplacements.value.find((r) => r.original === trueOriginal);
          return {
            trueOriginal,
            currentText: existing ? existing.updated : (line.change ? line.change.updated : line.text),
          };
        }),
    );

    // Click-to-edit directly on the rendered PDF: pdf.js's text layer places invisible,
    // precisely-positioned <span> elements over the canvas matching the real text, so a
    // click there tells us exactly which words were clicked — no separate backend
    // geometry needed, and it works for ANY bullet, not just ones the AI already changed.
    const handleTailoredPreviewClick = (event) => {
      const span = event.target.closest?.("span");
      const textLayer = span?.closest(".textLayer");
      const wrapper = tailoredPreviewWrapperRef.value;
      if (!span || !textLayer || !wrapper) return;

      // pdf.js splits one visual line across multiple spans — gather the ones at roughly
      // the same vertical position as the clicked span to reconstruct the full line.
      const clickedRect = span.getBoundingClientRect();
      const clickedCenterY = clickedRect.top + clickedRect.height / 2;
      const lineSpans = Array.from(textLayer.querySelectorAll("span")).filter((s) => {
        const r = s.getBoundingClientRect();
        return Math.abs(r.top + r.height / 2 - clickedCenterY) < Math.max(clickedRect.height, 4) * 0.6;
      });
      if (!lineSpans.length) return;
      lineSpans.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
      const clickedLineText = normalizeForMatch(lineSpans.map((s) => s.textContent).join(""));
      if (!clickedLineText) return;

      const match = tailoredResumeBulletIndex.value.find((entry) => {
        const normalizedCurrent = normalizeForMatch(entry.currentText);
        return normalizedCurrent.includes(clickedLineText) || clickedLineText.includes(normalizedCurrent);
      });
      if (!match) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const rects = lineSpans.map((s) => s.getBoundingClientRect());
      const lineBottom = Math.max(...rects.map((r) => r.bottom));
      const lineLeft = Math.min(...rects.map((r) => r.left));

      tailoredResumeEditingOriginal.value = match.trueOriginal;
      tailoredResumeEditingCurrentText.value = match.currentText;
      tailoredResumeEditPopoverPos.value = {
        top: lineBottom - wrapperRect.top + 4,
        left: lineLeft - wrapperRect.left,
      };
    };

    const saveTailoredEdit = (newText) => {
      if (tailoredResumeEditingOriginal.value === null) return;
      const trimmed = newText.trim() || tailoredResumeEditingOriginal.value;
      upsertTailoredReplacement(tailoredResumeEditingOriginal.value, trimmed);
      tailoredResumeEditingOriginal.value = null;
    };

    // Replacements changed (fresh generation, or a hand-edit) — the rendered PDF is now
    // stale, so drop it and re-render.
    watch(
      tailoredResumeReplacements,
      () => {
        tailoredResumePdfSource.value = null;
        if (tailoredResumeReplacements.value.length) {
          fetchTailoredResumePreview();
        }
      },
      { deep: true },
    );

    // Per-line render info for the preview pane: which lines the AI changed (so we can
    // highlight + word-diff them against the original bullet), and which are untouched.
    const tailoredResumeLines = computed(() => {
      const changesByUpdatedText = new Map();
      for (const c of tailoredResumeChanges.value) {
        changesByUpdatedText.set(c.updated.trim(), c);
      }
      const rawLines = tailoredResumeContent.value.split("\n");
      let seenNonEmpty = false;
      const built = rawLines.map((line, index) => {
        const parts = splitBulletLine(line);
        const change = parts ? changesByUpdatedText.get(parts.rest.trim()) : null;
        const kind = parts ? "bullet" : classifyResumeLine(rawLines, index, seenNonEmpty);
        if (line.trim()) seenNonEmpty = true;
        return {
          index,
          raw: line,
          kind,
          prefix: parts ? parts.indent + parts.marker + " " : "",
          text: parts ? parts.rest : line,
          change: change || null,
          diff: change ? diffWords(change.original, change.updated) : null,
        };
      });
      return built;
    });

    // Per-change diff info — currently only used to know whether any change has
    // suspicious/fabricated-looking terms, for the panel header's warning legend.
    const tailoredResumeChangesView = computed(() =>
      tailoredResumeChanges.value.map((c) => ({
        ...c,
        diff: diffWords(c.original, c.updated),
      })),
    );

    // A contenteditable div grows with its content exactly like any other text block — no
    // manual height math needed (a textarea's scrollHeight is unreliable to measure right as
    // it mounts inside a flex row, before layout has settled). Seed its text once on mount
    // and place the caret at the end so it doesn't jump to the start of the bullet.
    const initTailoredEditableDiv = (el, initialText) => {
      if (!el || el.dataset.tailoredInit) return;
      el.dataset.tailoredInit = "1";
      el.textContent = initialText;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    };

    // Keeps tailoredResumeReplacements (what "Download Modified" actually applies to the real
    // PDF/DOCX) in sync with whatever text ends up on-screen — otherwise a hand-edit here could
    // silently NOT show up in the downloaded file, which would defeat the point of editing at all.
    const upsertTailoredReplacement = (sourceOriginal, finalText) => {
      if (!sourceOriginal) return;
      const trimmed = finalText.trim();
      if (sourceOriginal.trim() === trimmed) {
        tailoredResumeReplacements.value = tailoredResumeReplacements.value.filter((r) => r.original !== sourceOriginal);
        return;
      }
      const existing = tailoredResumeReplacements.value.find((r) => r.original === sourceOriginal);
      if (existing) {
        existing.updated = trimmed;
      } else {
        tailoredResumeReplacements.value.push({ original: sourceOriginal, updated: trimmed });
      }
    };

    // Belt-and-suspenders close: the div's own @blur already saves on most clicks away,
    // but a document-level listener guarantees "click anywhere closes it" even if focus
    // handling gets weird (e.g. a click that doesn't actually move focus).
    const closeTailoredEditOnOutsideClick = (event) => {
      if (tailoredResumeEditingOriginal.value === null) return;
      const container = document.querySelector("[data-tailored-editing]");
      if (container?.contains(event.target)) return;
      const editable = container?.querySelector("[contenteditable]");
      if (editable) {
        saveTailoredEdit(editable.textContent);
      } else {
        tailoredResumeEditingOriginal.value = null;
      }
    };
    watch(tailoredResumeEditingOriginal, (val) => {
      if (val !== null) {
        document.addEventListener("mousedown", closeTailoredEditOnOutsideClick, true);
      } else {
        document.removeEventListener("mousedown", closeTailoredEditOnOutsideClick, true);
        tailoredResumeEditPopoverPos.value = null;
      }
    });
    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", closeTailoredEditOnOutsideClick, true);
    });

    const formattedTailoredResume = computed(() => {
      if (!tailoredResumeContent.value) return "";
      return DOMPurify.sanitize(md.render(tailoredResumeContent.value));
    });
    const activeApplyTab = ref("cover-letter");
    const resumePdfSource = ref(null);
    const loadingResumePdf = ref(false);
    const resumePdfPageCount = ref(0);

    const openExternalJobPost = () => {
      window.open(job.value.link, "_blank");
      if (!job.value.applied) {
        showExternalApplyModal.value = true;
      }
    };

    const markAsExternallyApplied = async () => {
      if (!authStore.state.isLoggedIn) {
        showExternalApplyModal.value = false;
        showLoginRegisterPrompt.value = true;
        return;
      }
      externalApplySubmitting.value = true;
      try {
        await apiClient.post("/job-applications/track/", {
          job_post_id: jobId.value,
        });
      } catch (_) {
        // Best-effort — still mark as applied in UI
      } finally {
        job.value.applied = true;
        if (Object.prototype.hasOwnProperty.call(job.value, "userHasApplied")) {
          job.value.userHasApplied = true;
        }
        externalApplySubmitting.value = false;
        showExternalApplyModal.value = false;
        showToastNotification("success", "Application tracked", "Great! This job has been marked as applied.");
      }
    };

    const openLinkedInJob = () => {
      // Gating: must be logged in

      if (!authStore.state.isLoggedIn) {
        showLoginRegisterPrompt.value = true;

        return;
      }

      // Removed subscription expiration check - all users have Free plan

      // Gating: must have completed onboarding

      if (
        authStore.state.user &&
        authStore.state.user.account_type === "jobseeker" &&
        !authStore.state.user.onboarding_completed
      ) {
        showOnboardingRequiredModal.value = true;

        return;
      }

      // All checks passed, open LinkedIn job

      if (job.value && job.value.link) {
        window.open(job.value.link, "_blank");
      }
    };

    return {
      coverLetterPanelOpen,
      coverLetterButtonLabel,
      onCoverLetterPanelApplied,
      showBackButton,
      backLabel,
      backPath,

      job,

      loading,

      error,

      route,

      similarJobs,

      loadingSimilar,

      jobId,

      userResumes,

      coverLetterDraftSourceLabel,

      showCoverLetterSurveyModal,

      surveyStars,

      surveyComment,

      surveySubmitting,

      dismissCoverLetterSurvey,

      submitCoverLetterSurvey,

      isSaved,

      employerInitials,

      formattedDescription,

      saveJob,

      showIntegrationModal,

      showCoverLetterRequiredModal,

      shareJob,

      copyToClipboard,

      shareVia,

      formatDate,

      formatPostedDateTime,

      jobSlug,

      jobUrl,

      showShareModal,

      copied,

      showApplicationErrorModal,

      applicationErrorOriginalJobUrl,

      // Navigation

      showToast,

      toastType,

      toastTitle,

      toastMessage,

      employerLogoUrl,

      logoLoadError,

      showToastNotification,

      goToIntegrationSettings,

      // New state for apply points

      oljConnected,
      oljNeedsReconnect,
      OLJ_RECONNECT_ROUTE,

      // Modal state

      showLoginRegisterPrompt,

      showOnboardingRequiredModal,

      handleApplyClick,

      handleLogin,

      handleRegister,

      handleCancelPrompt,

      handleOnboardingCancel,

      handleOnboardingComplete,

      openLinkedInJob,

      viewEmployerProfile,

      // Router and fetch function for error handling

      router,

      fetchJobDetails,

      getJobDetailPath,

      canUseAICoverLetterFeatures,
      isSubscriptionExpired,
      isSubscribedUser,

      canGenerateAiCoverLetterNow,

      generateWithAiButtonTitle,

      isFreeTrialAiLimited,

      trialAiQuotaRemaining,

      trialAiQuotaLimit,

      showAiCoverLetterUpgradeHint,
      expiredPlanLabel,
      isLoggedIn,
      isAdminUser,
      externalPlatformLabel,
      externalPlatformLogo,
      siteName,
      showApplyOnSite,
      isExternalJob,
      oljUnlocked,
      showExternalApplyModal,
      externalApplySubmitting,
      openExternalJobPost,
      markAsExternallyApplied,
      openModal,
      // Resume tailor
      generatingTailoredResume,
      tailoredResumeContent,
      tailoredResumeReplacements,
      tailoredResumeChanges,
      tailoredResumeRelevance,
      relevanceBadgeClass,
      tailoredResumeLines,
      tailoredResumeChangesView,
      initTailoredEditableDiv,
      tailoredResumeCopied,
      tailoredResumeTextareaRef,
      downloadingTailoredResume,
      downloadTailoredResume,
      tailoredResumePanelOpen,
      tailoredResumePdfSource,
      loadingTailoredResumePreview,
      tailoredResumePreviewUnavailable,
      tailoredPreviewWrapperRef,
      tailoredResumeEditPopoverPos,
      tailoredResumeEditingOriginal,
      tailoredResumeEditingCurrentText,
      handleTailoredPreviewClick,
      saveTailoredEdit,
      openTailoredResumePanel,
      closeTailoredResumePanel,
      generateTailoredResume,
      copyTailoredResume,
      activeApplyTab,
      resumePdfSource,
      loadingResumePdf,
      resumePdfPageCount,
      fetchResumePdf,
      formattedTailoredResume,
      jobDetailsTourActive,
      jobDetailsTourStepIndex,
      jobDetailsTourSteps,
      jobDetailsCurrentTourStep,
      jobDetailsTourHighlightClass,
      nextJobDetailsTourStep,
      previousJobDetailsTourStep,
      finishJobDetailsTour,
      showCloseJobDetailsTourConfirm,
      confirmCloseJobDetailsTour,
      cancelCloseJobDetailsTour,
      proceedCloseJobDetailsTour,
    };
  },
};
</script>

<style>
.tailored-panel-slide-enter-active,
.tailored-panel-slide-leave-active {
  transition: transform 0.25s ease;
}
.tailored-panel-slide-enter-from,
.tailored-panel-slide-leave-to {
  transform: translateX(100%);
}
.tailored-panel-fade-enter-active,
.tailored-panel-fade-leave-active {
  transition: opacity 0.25s ease;
}
.tailored-panel-fade-enter-from,
.tailored-panel-fade-leave-to {
  opacity: 0;
}

.prose {
  line-height: 1.7;
}

.prose p {
  margin-bottom: 1rem;
}

.prose ul {
  list-style-type: disc;

  padding-left: 1.5rem;

  margin-bottom: 1rem;
}

.prose ol {
  list-style-type: decimal;

  padding-left: 1.5rem;

  margin-bottom: 1rem;
}

.prose li {
  margin-bottom: 0.5rem;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4 {
  margin-top: 1.5rem;

  margin-bottom: 0.75rem;

  font-weight: 600;
}

.prose h2 {
  font-size: 1.5rem;
}

.prose h3 {
  font-size: 1.25rem;
}

.prose h4 {
  font-size: 1.125rem;
}

.prose strong {
  font-weight: 600;
}

.prose em {
  font-style: italic;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .apply-modal-inner {
    width: 100% !important;
    max-width: 100% !important;
  }
  .apply-modal-overlay {
    align-items: flex-start !important;
  }
}


</style>
